/* Cabinet data is not fetched from Dossiers API, 
   we're displaying it in a similar way to companies
*/

import { useMemo, useState } from 'react';
import type { CabinetSelfListEntry } from '../types/company';
import { __ } from '@/hub-shared/utils/i18n';
import { createCabinetFiscalClient } from '@tpct/mission-core/utils/cabinetFiscalApi';
import { useCabinetMissionStartWithFiscalModal } from '@tpct/mission-core/hooks/useCabinetMissionStartWithFiscalModal';
import CabinetFiscalPeriodModal from '@tpct/mission-core/components/CabinetFiscalPeriodModal';
import {
  buildVSMEFormUrl,
  fetchMissionSummary,
  startCabinetMission,
  type MissionStartPayload,
} from '../utils/vsmeApi';
import { TableRowActions, viewIcon, vsmeIconBlack } from '@tpct/admin-ui';
import type { TableRowAction } from '@tpct/admin-ui';
import { vsmeMissionsListRelativePath, vsmeMissionSetupRelativePath } from '@tpct/mission-core/utils/vsmePortalRoutes';

type CabinetSelfRowProps = {
  entry: CabinetSelfListEntry;
  navigateTo?: (path: string) => void;
  bootstrapKey?: string;
};

const formatCreationDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const hasCurrentMission = (entry: CabinetSelfListEntry): boolean =>
  (entry.current_mission?.id ?? 0) > 0;

const hasContinuableMission = (entry: CabinetSelfListEntry): boolean =>
  entry.current_mission?.can_continue === true && hasCurrentMission(entry);

const missionActionLabel = (entry: CabinetSelfListEntry): string => {
  if (hasContinuableMission(entry)) {
    return __('Continuer la mission');
  }
  if (hasCurrentMission(entry)) {
    return __('Voir les rapports VS (vsme)');
  }
  return __('Démarrer mission');
};

const reportsListPath = vsmeMissionsListRelativePath();

const CabinetSelfRow = ({
  entry,
  navigateTo,
  bootstrapKey = 'AccountingHubCompaniesAdmin',
}: CabinetSelfRowProps) => {
  const [isContinuingMission, setIsContinuingMission] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const fiscalClient = useMemo(
    () => createCabinetFiscalClient(bootstrapKey),
    [bootstrapKey],
  );

  const openMissionWizard = (missionId: number) => {
    const wizardPath = vsmeMissionSetupRelativePath(missionId);
    if (navigateTo) {
      navigateTo(wizardPath);
    } else {
      window.location.href = `/hub${wizardPath}`;
    }
  };

  const openReportsList = () => {
    if (navigateTo) {
      navigateTo(reportsListPath);
      return;
    }
    window.location.href = `/hub${reportsListPath}`;
  };

  const goToVsmeForm = (reportId: number, missionId: number) => {
    window.location.assign(buildVSMEFormUrl(String(reportId), 0, missionId));
  };

  const continueExistingMission = async (missionId: number) => {
    const summary = await fetchMissionSummary(missionId);
    if (summary.linked_report_id !== null && summary.linked_report_id > 0) {
      goToVsmeForm(summary.linked_report_id, missionId);
      return;
    }

    openMissionWizard(missionId);
  };

  const { runStartMission, isStarting, fiscalModalProps } = useCabinetMissionStartWithFiscalModal<MissionStartPayload>({
    fiscalClient,
    startMission: () => startCabinetMission(),
    onMissionStarted: (payload) => {
      setStartError(null);
      openMissionWizard(payload.mission.id);
    },
    onStartError: (message) => {
      setStartError(message);
    },
    prefillFromEntry: {
      fiscal_year_start: entry.fiscal_year_start,
      fiscal_year_end: entry.fiscal_year_end,
    },
  });

  const handleStartVSME = async () => {
    if (isStarting || isContinuingMission) {
      return;
    }

    if (hasCurrentMission(entry) && !hasContinuableMission(entry)) {
      openReportsList();
      return;
    }

    setStartError(null);

    try {
      if (hasContinuableMission(entry)) {
        const missionId = entry.current_mission?.id;
        if (missionId === undefined || missionId <= 0) {
          throw new Error(__('Mission introuvable pour ce cabinet.'));
        }

        setIsContinuingMission(true);
        await continueExistingMission(missionId);
        return;
      }

      await runStartMission();
    } catch (error) {
      console.error('Error starting cabinet mission:', error);
      setStartError(
        error instanceof Error
          ? error.message
          : __('Une erreur est survenue lors du démarrage de la mission VS.'),
      );
    } finally {
      setIsContinuingMission(false);
    }
  };

  const actions: TableRowAction[] = [
    ...(navigateTo ? [{
      key: 'view',
      label: __('Voir'),
      icon: viewIcon,
      onClick: () => navigateTo('/cabinet'),
    }] : []),
    {
      key: 'vsme',
      label: missionActionLabel(entry),
      icon: vsmeIconBlack,
      onClick: handleStartVSME,
      loading: isStarting || isContinuingMission,
      disabled: isStarting || isContinuingMission,
    },
  ];

  return (
    <>
      <tr className="companies-table__row companies-table__row--cabinet-self">
        <td className="companies-table__name">
          <p>{entry.name}</p>
        </td>
        <td className="companies-table__siret">
          {entry.siren}
        </td>
        <td className="companies-table__users">
          —
        </td>
        <td className="companies-table__created">
          {formatCreationDate(entry.created)}
        </td>
        <td className="companies-table__actions">
          {startError !== null ? (
            <p className="companies-table__inline-error" role="alert">
              {startError}
            </p>
          ) : null}
          <TableRowActions actions={actions} overflowLabel={__('Plus d’actions')} />
        </td>
      </tr>
      {fiscalModalProps.isOpen ? <CabinetFiscalPeriodModal {...fiscalModalProps} /> : null}
    </>
  );
};

export default CabinetSelfRow;
